import { TriangleAlert } from "lucide-react"
import { useState } from "react"

import {
  CUSTOM_INSTRUCTIONS_PLACEHOLDERS,
  CustomInstructionsField,
} from "@/components/agents/setup/custom-instructions"
import {
  CadenceFields,
  type CalendarConnect,
  HandoffFields,
} from "@/components/agents/setup/lead-responder/fields"
import {
  type CrmSource,
  type LeadIntake,
  SourcesFields,
} from "@/components/agents/setup/lead-responder/sources-step"
import type { LeadResponderSetupData } from "@/components/agents/setup/lead-responder/types"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Tabs,
  TabsBar,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"

type A2pStatus = "not_registered" | "pending" | "approved"

// The agent's current config, prefilled from the server in the real build.
export const DEFAULT_LEAD_RESPONDER_DATA: LeadResponderSetupData = {
  sources: [],
  crm_provider: "",
  crm_scope_value: "",
  channels: ["voice", "sms"],
  booking_enabled: true,
  transfer_enabled: false,
  booking_calendar: "",
  booking_event_type: "",
  transfer_number: "",
  transfer_hours: [
    { day: "Monday", abbr: "Mon", open: true, from: "09:00", to: "17:00" },
    { day: "Tuesday", abbr: "Tue", open: true, from: "09:00", to: "17:00" },
    { day: "Wednesday", abbr: "Wed", open: true, from: "09:00", to: "17:00" },
    { day: "Thursday", abbr: "Thu", open: true, from: "09:00", to: "17:00" },
    { day: "Friday", abbr: "Fri", open: true, from: "09:00", to: "17:00" },
    { day: "Saturday", abbr: "Sat", open: false, from: "09:00", to: "17:00" },
    { day: "Sunday", abbr: "Sun", open: false, from: "09:00", to: "17:00" },
  ],
  speed_target: "asap",
  persistence: "standard",
  consent_attested: false,
  custom_instructions: "",
  sms_area_code: "",
  selected_phone_number: "",
}

export function LeadResponderEditSheet({
  open,
  onOpenChange,
  agentName = "Lead Responder",
  a2pStatus,
  onStartRegistration,
  initial,
  leadIntake,
  crm,
  crmLists,
  calendars,
  onSave,
  saving = false,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  agentName?: string
  // IMP-423/462: accepted for call-site compatibility; the Channels tab that
  // consumed it moved to Settings → Identity with the rest of the identity.
  textingEnabled?: boolean
  a2pStatus: A2pStatus
  onStartRegistration: () => void
  initial?: Partial<LeadResponderSetupData>
  // IMP-371: per-agent intake artifacts + CRM connection state, so sources can
  // be reconfigured after launch — same data the setup Sources step uses.
  leadIntake?: LeadIntake
  crm?: CrmSource
  // IMP-419: top-level deferred CRM lists/filters (undefined until loaded),
  // merged into crm.lists for the Sources tab.
  crmLists?: { id: string; name: string }[]
  // IMP-409: real calendar connection state + live event types for the Handoff
  // tab (was hardcoded mock).
  calendars: CalendarConnect
  onSave: (data: LeadResponderSetupData) => void
  saving?: boolean
}) {
  const [data, setData] = useState<LeadResponderSetupData>({
    ...DEFAULT_LEAD_RESPONDER_DATA,
    ...initial,
  })
  const update = (patch: Partial<LeadResponderSetupData>) =>
    setData((prev) => ({ ...prev, ...patch }))

  const textOn = data.channels.includes("sms")

  // The one piece unique to the edit drawer: enabling text surfaces the SMS
  // registration state, which the setup channels step doesn't carry.
  const registrationAlert =
    textOn && a2pStatus !== "approved" ? (
      <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-400/30 dark:bg-amber-400/10">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Texting needs SMS registration</p>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {a2pStatus === "pending"
              ? "Your registration is under review. Texting turns on once it's approved."
              : "Until you register, the agent calls leads but won't text them."}
          </p>
        </div>
        {a2pStatus === "not_registered" && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={onStartRegistration}
          >
            Register
          </Button>
        )}
      </div>
    ) : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="px-6 py-4">
          <SheetTitle>Edit {agentName}</SheetTitle>
        </SheetHeader>

        <Tabs
          defaultValue="sources"
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <TabsBar>
            <TabsList variant="line">
              <TabsTrigger value="sources">Sources</TabsTrigger>
              <TabsTrigger value="qualifying">Cadence</TabsTrigger>
              <TabsTrigger value="handoff">Handoff</TabsTrigger>
              <TabsTrigger value="customization">Customization</TabsTrigger>
            </TabsList>
          </TabsBar>

          <TabsContent
            value="sources"
            className="flex-1 space-y-6 overflow-y-auto px-6 py-5"
          >
            {registrationAlert}
            <SourcesFields
              data={data}
              onUpdate={update}
              leadIntake={leadIntake}
              crm={crm ? { ...crm, lists: crmLists } : undefined}
            />
          </TabsContent>
          <TabsContent
            value="qualifying"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <CadenceFields data={data} onUpdate={update} showSpeed={false} />
          </TabsContent>
          <TabsContent
            value="handoff"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <HandoffFields
              data={data}
              onUpdate={update}
              calendars={calendars}
            />
          </TabsContent>
          <TabsContent
            value="customization"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <CustomInstructionsField
              value={data.custom_instructions}
              placeholder={CUSTOM_INSTRUCTIONS_PLACEHOLDERS["lead-responder"]}
              onChange={(v) => update({ custom_instructions: v })}
            />
          </TabsContent>
        </Tabs>

        <SheetFooter className="border-t px-6 py-4">
          <Button
            className="w-full rounded-sm"
            disabled={saving}
            onClick={() => onSave(data)}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
